(() => {
  'use strict';

  if (window.LplP90Triggers) {
    window.LplP90Triggers.refresh();
    return;
  }

  const componentSelector = 'lpl-purchase-options';
  const radioSelector = '[data-lpl-mode="ninety_subscription"], [data-lpl-mode="ninety_onetime"]';
  const components = new Map();
  const entries = new Map();
  let frame = 0;

  const schedule = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      refresh();
    });
  };

  const resizeObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver(schedule)
    : null;

  function setStyle(element, property, value) {
    if (element.style[property] !== value) element.style[property] = value;
  }

  function pixels(value) {
    return `${Math.round(value * 1000) / 1000}px`;
  }

  function forget(entry, unwrap = false) {
    resizeObserver?.unobserve(entry.row);
    if (entry.placeholder) {
      resizeObserver?.unobserve(entry.placeholder);
      entry.placeholder.style.removeProperty('visibility');
    }
    entry.slot?.removeAttribute('data-lpl-p90-enhanced');
    entry.trigger.remove();
    entry.radio.classList.remove('lpl-p90__radio');
    if (unwrap && entry.row.parentNode && entry.radio.parentNode === entry.row) {
      entry.row.replaceWith(entry.radio);
    }
    entries.delete(entry.radio);
  }

  function position(entry) {
    const { row, placeholder, trigger } = entry;
    const bounds = placeholder.getBoundingClientRect();
    const rowBounds = row.getBoundingClientRect();
    const visible = bounds.width > 0 && bounds.height > 0 && rowBounds.width > 0;

    // The one-time accordion may be closed. Do not leave a focusable overlay
    // behind when its corresponding purchase option has no layout box.
    trigger.hidden = !visible;
    setStyle(trigger, 'display', visible ? '' : 'none');
    if (!visible) return;

    // The real button sits outside the radio. Mirror the note's computed type
    // so it looks exactly like the original inherited, underlined text link.
    const type = window.getComputedStyle(placeholder);
    ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight', 'letterSpacing', 'color'].forEach(property => {
      setStyle(trigger, property, type[property]);
    });
    const hitHeight = Math.max(44, bounds.height);
    // Keep the expanded target out of the preceding Includes bullet. Extra
    // space goes into the row's lower padding while the text stays in place.
    const extraHeight = hitHeight - bounds.height;
    const spaceAbove = Math.min(7, extraHeight / 2);
    setStyle(trigger, 'left', pixels(bounds.left - rowBounds.left));
    setStyle(trigger, 'top', pixels(bounds.top - rowBounds.top - spaceAbove));
    setStyle(trigger, 'width', pixels(bounds.width));
    setStyle(trigger, 'height', pixels(hitHeight));
    setStyle(trigger, 'paddingTop', pixels(spaceAbove));
    setStyle(trigger, 'paddingBottom', pixels(extraHeight - spaceAbove));
    setStyle(placeholder, 'visibility', 'hidden');
    entry.slot.setAttribute('data-lpl-p90-enhanced', '');
  }

  function mount(radio) {
    const slot = radio.querySelector('[data-lpl-p90-slot]');
    const placeholder = slot?.querySelector('.lpl-p90__trigger');
    let entry = entries.get(radio);

    if (!placeholder) {
      if (entry) forget(entry, true);
      return;
    }

    if (!entry) {
      let row = radio.parentElement;
      if (!row?.classList.contains('lpl-p90__row')) {
        row = document.createElement('div');
        row.className = 'lpl-p90__row';
        radio.before(row);
        row.append(radio);
      }
      row.dataset.lplP90Mode = radio.dataset.lplMode;
      setStyle(row, 'position', 'relative');
      setStyle(row, 'display', 'block');
      radio.classList.add('lpl-p90__radio');

      let trigger = Array.from(row.children).find((child) =>
        child.classList.contains('lpl-p90__floating-trigger')
      );
      if (!trigger) {
        trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'lpl-p90__trigger lpl-p90__floating-trigger';
        trigger.setAttribute('data-lpl-p90-trigger', '');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', 'lpl-p90-dialog');
        trigger.setAttribute('aria-expanded', 'false');
        row.append(trigger);
      }
      trigger.dataset.mode = radio.dataset.lplMode;
      trigger.dataset.lplP90Mode = radio.dataset.lplMode;
      setStyle(trigger, 'position', 'absolute');
      setStyle(trigger, 'margin', '0px');
      setStyle(trigger, 'zIndex', '1');

      entry = { radio, row, trigger, slot: null, placeholder: null };
      entries.set(radio, entry);
      resizeObserver?.observe(row);
    }

    if (entry.placeholder !== placeholder) {
      if (entry.placeholder) resizeObserver?.unobserve(entry.placeholder);
      entry.slot = slot;
      entry.placeholder = placeholder;
      resizeObserver?.observe(placeholder);
    }

    const label = placeholder.textContent.trim();
    if (entry.trigger.textContent !== label) entry.trigger.textContent = label;
    position(entry);
  }

  function observe(component) {
    if (components.has(component)) return;
    const observer = new MutationObserver((records) => {
      // Purchase refresh() replaces note.innerHTML. Ignore our sibling button,
      // wrappers and visibility attributes so enhancement cannot observe itself
      // in a perpetual mutation loop.
      if (records.some(({ target, addedNodes, removedNodes }) => {
        if (target instanceof Element && target.closest('[data-lpl-option-note]')) return true;
        return [...addedNodes, ...removedNodes].some((node) =>
          node instanceof Element && node.matches(radioSelector)
        );
      })) schedule();
    });
    observer.observe(component, { childList: true, subtree: true });
    components.set(component, observer);
  }

  function findComponents(root) {
    if (root instanceof Element && root.matches(componentSelector)) observe(root);
    root.querySelectorAll?.(componentSelector).forEach(observe);
  }

  function refresh(root = document) {
    findComponents(root);
    for (const [component, observer] of components) {
      if (!component.isConnected) {
        observer.disconnect();
        components.delete(component);
        continue;
      }
      component.querySelectorAll(radioSelector).forEach(mount);
    }
    for (const entry of entries.values()) {
      if (!entry.radio.isConnected || !entry.radio.closest(componentSelector)) forget(entry);
    }
    document.dispatchEvent(new CustomEvent('lpl-p90:triggers-updated'));
  }

  window.LplP90Triggers = { refresh };

  // A theme-editor section or product-info replacement can insert an entirely
  // new component. Only inspect added component subtrees, not unrelated page
  // mutations such as opening the portaled education dialog.
  const documentObserver = new MutationObserver((records) => {
    let changed = false;
    for (const { addedNodes, removedNodes } of records) {
      for (const node of addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(componentSelector) || node.querySelector(componentSelector)) {
          findComponents(node);
          changed = true;
        }
      }
      if ([...removedNodes].some((node) => node instanceof Element && (
        node.matches(componentSelector) || node.querySelector(componentSelector)
      ))) changed = true;
    }
    if (changed) schedule();
  });
  documentObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('resize', schedule, { passive: true });
  document.fonts?.ready.then(schedule);
  refresh();
})();
